import { Component, EventEmitter, HostListener, Input, OnDestroy, OnInit, Output, effect, computed, ChangeDetectionStrategy } from '@angular/core';

import { SortTableComponent } from '../../../../../lib/components/sort-table/sort-table.component';
import { TileEditorComponent } from '../../../../../lib/components/tile-editor/tile-editor.component';
import { FileDownloadUtil } from '../../../../../shared/utils/file-download.util';
import {
  TileProcessingItem,
  TileProcessingStatus,
  ProcessingState
} from '../../models/csv-tile';
import { BulkTileCreationService } from '../../../creation/services/bulk-tile-creation.service';
import { BulkTileConfigurationService } from '../../../configuration/services/bulk-tile-configuration.service';
import { CsvTileImporterService } from '../../services/csv-tile-importer.service';
import { CsvFieldId } from '../../constants/csv-columns.constants';
import { BehaviorSubject, Subscription, from, of } from 'rxjs';
import { mergeMap, map, catchError, tap, finalize, takeWhile } from 'rxjs/operators';
import { SortTable, SortTableRow } from '../../../../../shared/models/sort-table/types';
import {
  SortTableCellIcon,
  SortTableCellTextPill,
  SortTableCellDisplayName,
  SortTableCellButtons,
  SortTableCellSwatch
} from '../../../../../shared/models/sort-table/cell-classes';
import { OptionGroupMultiSelect } from '../../../../../shared/models/option';
import { DialogBoxButtonColor } from '../../../../../shared/models/dialog-box';
import { ObjectType } from '../../../../../shared/models/object.model';
import { Permission } from '../../../../../shared/models/fga/permission';
import { PermissionsService } from '../../../../../core/services/permissions.service';
import { SessionService } from '../../../../../core/services/session.service';
import { ShadeToolsService } from '../../../../../core/services/shade-tools.service';
import { TilesService } from '../../../../../core/services/tiles.service';
import { BulkModalService } from '@features/tile-bulk-operations/shared/services/bulk-modal.service';
import { TileId } from '../../../../../shared/models/tile';
import { EMPTY_ID } from '../../../../../shared/constants/empty-id';

// Column width constants for better maintainability
const COLUMN_WIDTHS = {
  STATUS: 80,
  DISPLAY_NAME: 200, // acts as min-width
  BUTTONS: 200,
  TYPE: 100,
  COLOR: 80,
  ICON: 60,
  APPS: 80,
  SUPER_TILES: 80,
} as const;

type StatusColor = 'violet' | 'indigo' | 'green' | 'red' | 'yellow' | 'gray';

const STATUS_CONFIG: Record<TileProcessingStatus, {
  icon: string;
  color: StatusColor;
  name: string;
  defaultMessage: string
}> = {
  [TileProcessingStatus.CREATED]: {
    icon: 'circle-check',
    color: 'green',
    name: 'Created',
    defaultMessage: 'Tile successfully created'
  },
  [TileProcessingStatus.UPDATED]: {
    icon: 'circle-check',
    color: 'green',
    name: 'Updated',
    defaultMessage: 'Tile successfully updated'
  },
  [TileProcessingStatus.PROCESSING]: {
    icon: 'loader',
    color: 'indigo',
    name: 'Processing',
    defaultMessage: 'Tile is currently being processed'
  },
  [TileProcessingStatus.LINKING_APPS]: {
    icon: 'link',
    color: 'violet',
    name: 'Linking Apps',
    defaultMessage: 'Linking tile to applications'
  },
  [TileProcessingStatus.AWAITING_FOLDER_LINKING]: {
    icon: 'hourglass',
    color: 'indigo',
    name: 'Awaiting Folder Linking',
    defaultMessage: 'Waiting for folder linking phase'
  },
  [TileProcessingStatus.LINKING_SUPER_TILES]: {
    icon: 'folder-tree',
    color: 'violet',
    name: 'Linking Super Tiles',
    defaultMessage: 'Linking tile to super tiles'
  },
  [TileProcessingStatus.LINKING_TAGS]: {
    icon: 'tag',
    color: 'violet',
    name: 'Linking Tags',
    defaultMessage: 'Assigning tags and facets to tile'
  },
  [TileProcessingStatus.QUEUED]: {
    icon: 'clock',
    color: 'gray',
    name: 'Queued',
    defaultMessage: 'Tile is waiting to be processed'
  },
  [TileProcessingStatus.ERROR_PERMISSIONS]: {
    icon: 'lock',
    color: 'red',
    name: 'Permission Error',
    defaultMessage: 'You do not have permission to add tiles to this device'
  },
  [TileProcessingStatus.ERROR_GENERAL]: {
    icon: 'circle-xmark',
    color: 'red',
    name: 'Error',
    defaultMessage: 'Failed to create tile'
  },
  [TileProcessingStatus.ERROR_APP_LINKING]: {
    icon: 'link-slash',
    color: 'red',
    name: 'App Linking Error',
    defaultMessage: 'Failed to link tile to applications'
  },
  [TileProcessingStatus.ERROR_FOLDER_LINKING]: {
    icon: 'folder-xmark',
    color: 'red',
    name: 'Folder Linking Error',
    defaultMessage: 'Failed to link tile to folders'
  },
  [TileProcessingStatus.ERROR_LINKING]: {
    icon: 'link-slash',
    color: 'red',
    name: 'Linking Error',
    defaultMessage: 'Failed to link tile to apps and super tiles'
  },
  [TileProcessingStatus.ERROR_TAG_LINKING]: {
    icon: 'tag',
    color: 'yellow',
    name: 'Tag Linking Error',
    defaultMessage: 'Tile created but tag/facet assignment failed'
  },
  [TileProcessingStatus.WARNING_PARTIAL]: {
    icon: 'triangle-exclamation',
    color: 'yellow',
    name: 'Warning',
    defaultMessage: 'Tile created but some operations failed.'
  }
} as const;

@Component({
  selector: 'motn-bulk-processing',
  templateUrl: './bulk-processing.component.html',
  styleUrls: ['./bulk-processing.component.scss'],
  imports: [
    SortTableComponent,
    TileEditorComponent
],
  providers: [
    BulkTileCreationService,
    BulkTileConfigurationService
  ],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true
})
export class BulkProcessingComponent implements OnInit, OnDestroy {
  @Input() processingItems: TileProcessingItem[] = [];
  @Input() deviceId?: string;
  @Input() isConfigurationMode: boolean = false; // New input to determine if we're updating or creating
  @Output() close = new EventEmitter<void>();
  @Output() goBack = new EventEmitter<void>();

  // Computed to select the right service
  readonly statistics = computed(() => {
    return this.isConfigurationMode
      ? this.bulkTileConfigService.statistics()
      : this.bulkTileCreationService.statistics();
  });


  // Getter to access the current service based on mode
  get currentService() {
    return this.isConfigurationMode
      ? this.bulkTileConfigService
      : this.bulkTileCreationService;
  }

  get hasRetryableItems(): boolean {
    return this.currentService.processingItems().some(item => [
      TileProcessingStatus.ERROR_GENERAL,
      TileProcessingStatus.ERROR_PERMISSIONS,
      TileProcessingStatus.ERROR_APP_LINKING,
      TileProcessingStatus.ERROR_FOLDER_LINKING,
      TileProcessingStatus.ERROR_LINKING,
      TileProcessingStatus.WARNING_PARTIAL
    ].includes(item.status));
  }

  table$: BehaviorSubject<SortTable> = new BehaviorSubject<SortTable>({
    css: { width: '100%' },
    columns: [],
    rows: []
  });

  private readonly paginationConfig = {
    enabled: true as const,
    rowsPerPage: 50,
    startOnPage: 1,
    maxPageOptions: 10
  };

  private readonly tableCss = { 'min-width': COLUMN_WIDTHS.STATUS + COLUMN_WIDTHS.DISPLAY_NAME + COLUMN_WIDTHS.BUTTONS + COLUMN_WIDTHS.TYPE + COLUMN_WIDTHS.COLOR + COLUMN_WIDTHS.ICON + COLUMN_WIDTHS.APPS + COLUMN_WIDTHS.SUPER_TILES + 'px' };

  // Build table columns dynamically based on mode
  private getTableColumns(): any[] {
    const baseColumns = [
      {
        name: 'Status',
        css: {
          'width': COLUMN_WIDTHS.STATUS + 'px',
          'justify-content': 'center'
        }
      },
      {
        name: 'Display Name',
        css: {
          'flex': 1,
          'min-width': COLUMN_WIDTHS.DISPLAY_NAME + 'px'
        }
      },
      {
        name: '',
        css: {
          'width': COLUMN_WIDTHS.BUTTONS + 'px',
          'justify-content': 'center'
        }
      },
      {
        name: 'Type',
        css: {
          'width': COLUMN_WIDTHS.TYPE + 'px',
          'justify-content': 'center'
        }
      },
      {
        name: 'Color',
        css: {
          'width': COLUMN_WIDTHS.COLOR + 'px',
          'justify-content': 'center'
        }
      },
      {
        name: 'Icon',
        css: {
          'width': COLUMN_WIDTHS.ICON + 'px',
          'justify-content': 'center'
        }
      }
    ];

    // Only show Apps and Super Tiles columns for creation mode
    if (!this.isConfigurationMode) {
      baseColumns.push(
        {
          name: 'Apps',
          css: {
            'width': COLUMN_WIDTHS.APPS + 'px',
            'justify-content': 'center'
          }
        },
        {
          name: 'Super Tiles',
          css: {
            'width': COLUMN_WIDTHS.SUPER_TILES + 'px',
            'justify-content': 'center'
          }
        }
      );
    }

    return baseColumns;
  }

  private currentOptionGroup: OptionGroupMultiSelect | null = null;
  editingTile: TileId = EMPTY_ID;

  // Computed table configuration - only recalculates when items change
  private readonly tableConfig = computed(() => {
    const items = this.isConfigurationMode
      ? this.bulkTileConfigService.processingItems()
      : this.bulkTileCreationService.processingItems();
    return this.buildTableFromItems(items);
  });

  constructor(
    public readonly bulkTileCreationService: BulkTileCreationService,
    public readonly bulkTileConfigService: BulkTileConfigurationService,
    private csvMapperService: CsvTileImporterService,
    private permissionsService: PermissionsService,
    private session: SessionService,
    private shadeToolsService: ShadeToolsService,
    private tilesService: TilesService,
    private bulkModalService: BulkModalService
  ) {
    // Emit table updates when computed config changes
    effect(() => {
      this.table$.next(this.tableConfig());
    });
  }

  ngOnInit(): void {
    // Effect handles initial table build automatically
    if (this.isConfigurationMode) {
      this.bulkTileConfigService.startProcessing(this.processingItems);
    } else {
      this.bulkTileCreationService.startProcessing(this.processingItems, this.deviceId);
    }
  }

  ngOnDestroy(): void {
    // Stop processing completely when component is destroyed
    if (this.isConfigurationMode) {
      this.bulkTileConfigService.stopProcessing();
    } else {
      this.bulkTileCreationService.stopProcessing();
    }
  }

  private buildTableFromItems(items: TileProcessingItem[]): SortTable {
    const table: SortTable = {
      css: this.tableCss,
      columns: this.getTableColumns(),
      rows: [],
      pagination: this.paginationConfig
    };

    // Keep items in the order they were added (processing order)
    const rows: SortTableRow[] = items.map((item) => {
      // Use tile object if available, otherwise use CSV data
      const displayName = item.tile?.displayName || item.csvData['Display Name'] || item.csvData['Tile Name'];
      const tileName = item.tile?.Name || item.csvData['Tile Name'];
      const color = item.tile?.Appearance?.Color || item.csvData['Color'] || '';
      const icon = item.tile?.Appearance?.Icon || item.csvData['Icon'] || 'circle';

      const tileType = this.csvMapperService.detectTileType(item.csvData) === 'toggle' ? 'Toggle' : 'Numeric';
      const actionButtons = this.createTileActionButtons(item);
      const statusTooltip = this.getStatusTooltip(item);
      const statusCell = new SortTableCellIcon(
        this.getStatusIcon(item.status),
        this.getStatusColor(item.status),
        statusTooltip
      );

      // Build base cells
      const cells: any[] = [
        statusCell,
        new SortTableCellDisplayName(displayName),
        new SortTableCellButtons(actionButtons),
        new SortTableCellTextPill(tileType, ''),
        new SortTableCellSwatch(color as any),
        new SortTableCellIcon(icon)
      ];

      // Only add apps and super tiles cells for creation mode
      if (!this.isConfigurationMode) {
        cells.push(
          new SortTableCellTextPill(
            this.getLinkedAppsDisplay(item),
            ''
          ),
          new SortTableCellTextPill(
            this.getLinkedSuperTilesDisplay(item),
            ''
          )
        );
      }

      return {
        context: `${item.id}-${item.status}-${displayName}-${icon}-${color}`,
        cells
      };
    });

    table.rows = rows;
    return table;
  }

  private getStatusIcon(status: TileProcessingStatus): string {
    return STATUS_CONFIG[status]?.icon || 'circle';
  }

  private getStatusColor(status: TileProcessingStatus): StatusColor | undefined {
    return STATUS_CONFIG[status]?.color;
  }


  private getStatusTooltip(item: TileProcessingItem): string {
    const config = STATUS_CONFIG[item.status];
    const statusName = config?.name || 'Unknown';
    const message = item.error || config?.defaultMessage || 'Unknown status';
    const linkingDetails = this.getLinkingDetailsHtml(item);

    return `
      <div class="tooltip-header">${statusName}</div>
      <div class="tooltip-message">${message}</div>
      ${linkingDetails}
    `;
  }

  private getLinkingDetailsHtml(item: TileProcessingItem): string {
    const shouldShowDetails = item.status === TileProcessingStatus.CREATED ||
                               item.status === TileProcessingStatus.UPDATED ||
                               item.status === TileProcessingStatus.WARNING_PARTIAL;
    if (!shouldShowDetails) return '';

    // For configuration mode, show linked and unlinked apps separately
    if (this.isConfigurationMode) {
      return this.getConfigurationLinkingDetailsHtml(item);
    }

    // For creation mode, show the original linking behavior
    const linkedApps = item.linkedApps || [];
    const linkedSuperTiles = item.linkedSuperTiles || [];
    const requestedApps = this.csvMapperService.parseLinkedAppIds(item.csvData['Linked App IDs']);
    const requestedSuperTiles = this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]);

    if (requestedApps.length === 0 && requestedSuperTiles.length === 0) return '';

    const appsHtml = requestedApps.length > 0
      ? this.buildLinkingSection('Linked Apps', linkedApps, requestedApps.length)
      : '';

    const superTilesHtml = requestedSuperTiles.length > 0
      ? this.buildLinkingSection('Linked Super Tiles', linkedSuperTiles, requestedSuperTiles.length)
      : '';

    const separator = appsHtml && superTilesHtml ? '<br>' : '';

    return `<div class="tooltip-linking-details">${appsHtml}${separator}${superTilesHtml}</div>`;
  }

  private getConfigurationLinkingDetailsHtml(item: TileProcessingItem): string {
    const sections: string[] = [];

    if (item.linkedApps?.length) {
      sections.push(this.buildLinkingSection('Linked Apps', item.linkedApps, item.linkedApps.length));
    }

    if (item.unlinkedApps?.length) {
      sections.push(this.buildLinkingSection('Unlinked Apps', item.unlinkedApps, item.unlinkedApps.length));
    }

    const requestedFolders = this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]);
    if (requestedFolders.length > 0) {
      sections.push(this.buildLinkingSection('Added to Folders', item.linkedSuperTiles || [], requestedFolders.length));
    }

    if (sections.length === 0) return '';

    return `<div class="tooltip-linking-details">${sections.join('<br>')}</div>`;
  }

  private buildLinkingSection(label: string, linkedIds: string[], requestedCount: number): string {
    const idsHtml = linkedIds.length > 0
      ? linkedIds.map(id => `<code>${id}</code>`).join(', ')
      : '<em>none</em>';

    return `<strong>${label} (${linkedIds.length}/${requestedCount}):</strong><br>${idsHtml}`;
  }

  private getLinkedAppsDisplay(item: TileProcessingItem): string {
    return this.getLinkedItemsDisplay(
      this.csvMapperService.parseLinkedAppIds(item.csvData['Linked App IDs']).length,
      item.linkedApps?.length || 0
    );
  }

  private getLinkedSuperTilesDisplay(item: TileProcessingItem): string {
    return this.getLinkedItemsDisplay(
      this.csvMapperService.parseLinkedSuperTileIds(item.csvData[CsvFieldId.LINKED_SUPER_TILE_IDS]).length,
      item.linkedSuperTiles?.length || 0
    );
  }

  private getLinkedItemsDisplay(requested: number, successful: number): string {
    return requested === 0 ? '-' : `${successful}/${requested}`;
  }

  pauseProcessing(): void {
    this.currentService.pauseProcessing();
  }

  retryFailed(): void {
    const items = this.currentService.processingItems();
    const failedItems = items.filter(
      item => [
        TileProcessingStatus.ERROR_GENERAL,
        TileProcessingStatus.ERROR_PERMISSIONS,
        TileProcessingStatus.ERROR_APP_LINKING,
        TileProcessingStatus.ERROR_FOLDER_LINKING,
        TileProcessingStatus.ERROR_LINKING,
        TileProcessingStatus.WARNING_PARTIAL
      ].includes(item.status)
    );

    // Reset failed items to QUEUED and clear errors
    failedItems.forEach(item => {
      const originalStatus = item.status;

      item.status = TileProcessingStatus.QUEUED;
      item.error = undefined;

      // Only clear tileId for creation errors (tile was never created)
      // Keep tileId for linking errors and partial successes (tile exists, just linking failed)
      const isLinkingError = [
        TileProcessingStatus.ERROR_APP_LINKING,
        TileProcessingStatus.ERROR_FOLDER_LINKING,
        TileProcessingStatus.ERROR_LINKING,
        TileProcessingStatus.WARNING_PARTIAL
      ].includes(originalStatus);

      if (!isLinkingError) {
        item.tileId = undefined;
      }
    });

    // Update the items signal to trigger UI update
    this.currentService.processingItems.set([...items]);

    // Resume processing (will restart processing for QUEUED items)
    this.currentService.resumeProcessing();
  }

  @HostListener('window:keydown.escape', ['$event'])
  onEscapeKey(event: KeyboardEvent): void {
    event.preventDefault();
    this.onClose(this.statistics().created);
  }

  onClose(created: number): void {
    // Check the appropriate service based on mode
    const isProcessingComplete = this.isConfigurationMode
      ? this.bulkTileConfigService.isCompleted()
      : this.bulkTileCreationService.isCompleted();

    // Close immediately if processing is complete
    if (isProcessingComplete) {
      this.closeDialog();
      return;
    }

    const confirmationMessage = this.getCloseConfirmationMessage(created);
    if (confirmationMessage) {
      this.shadeToolsService.startDialog(
        this.getCloseConfirmationHeader(),
        confirmationMessage,
        [
          {
            text: 'Close',
            color: DialogBoxButtonColor.Red,
            callback: () => {
              this.closeDialog();
            }
          },
          {
            text: 'Cancel',
            color: DialogBoxButtonColor.Alt,
            callback: () => {
              // Just close the dialog, don't do anything
            }
          }
        ],
        false, false, true, true
      );
    }
  }

  private getCloseConfirmationHeader(): string {
    const isProcessing = this.isConfigurationMode
      ? this.bulkTileConfigService.isProcessing()
      : this.bulkTileCreationService.isProcessing();

    const isPaused = this.isConfigurationMode
      ? this.bulkTileConfigService.isPaused()
      : this.bulkTileCreationService.isPaused();

    if (isProcessing || isPaused) {
      return this.isConfigurationMode
        ? '<i class="fa-regular fa-circle-stop"></i> Stop Configuration?'
        : '<i class="fa-regular fa-circle-stop"></i> Stop Processing?';
    }
    return '<i class="fa-solid fa-xmark"></i> Close Dialog?';
  }

  private getCloseConfirmationMessage(_created: number): string | null {
    const isProcessing = this.isConfigurationMode
      ? this.bulkTileConfigService.isProcessing()
      : this.bulkTileCreationService.isProcessing();

    const isPaused = this.isConfigurationMode
      ? this.bulkTileConfigService.isPaused()
      : this.bulkTileCreationService.isPaused();

    if (isProcessing || isPaused) {
      const status = isPaused ? 'paused' : 'in progress';
      if (this.isConfigurationMode) {
        return `Configuration updates are ${status}. This will stop the configuration process and close the dialog. ` +
               'Any tiles already updated will keep their new configuration.';
      } else {
        return `Processing is ${status}. This will stop the processing and close the dialog. ` +
               'Any tiles already created will remain and can be managed in the Tiles Existing page.';
      }
    }

    // No confirmation needed if processing is complete
    return null;
  }

  private closeDialog(): void {
    this.currentService.stopProcessing();
    this.close.emit();
  }

  onGoBack(): void {
    // If processing is complete, go back directly without confirmation
    if (!this.currentService.isProcessing()) {
      this.goBack.emit();
      return;
    }

    // If still processing, show confirmation dialog
    const message = this.isConfigurationMode
      ? 'This will stop the current configuration and return to the CSV upload screen. ' +
        'Any tiles already configured will keep their new settings.'
      : 'This will stop the current processing and return to the CSV upload screen. ' +
        'Any tiles already created will remain and can be managed in the Tiles Existing page.';

    this.shadeToolsService.startDialog(
      '<i class="fa-light fa-arrow-left"></i> Upload New CSV?',
      message,
      [
        {
          text: 'Yes, Go Back',
          color: DialogBoxButtonColor.Default,
          callback: () => {
            this.goBack.emit();
          }
        },
        {
          text: 'Cancel',
          color: DialogBoxButtonColor.Alt,
          callback: () => {
            // Just close the dialog, don't do anything
          }
        }
      ],
      false, false, true, true
    );
  }

  exportResults(): void {
    const csvContent = this.generateCSVReport();
    const filename = `tile-processing-results-${Date.now()}.csv`;
    FileDownloadUtil.downloadCsv(csvContent, filename).subscribe();
  }

  private generateCSVReport(): string {
    const headers = ['Tile Name', 'Status', 'Tile ID', 'Error Message'];

    const items = this.isConfigurationMode
      ? this.bulkTileConfigService.processingItems()
      : this.bulkTileCreationService.processingItems();

    const rows = items.map(item => [
      item.csvData['Tile Name'],
      item.status,
      item.tileId || '',
      item.error || ''
    ]);

    return [headers, ...rows]
      .map(row => row.map(cell => `"${cell}"`).join(','))
      .join('\n');
  }

  tableRowsRendered(_rows: SortTableRow[]): void {
    // Callback required by SortTableComponent
  }

  private createTileActionButtons(item: TileProcessingItem): any[] {
    const buttons: any[] = [];

    // Show buttons for any tile that was successfully created (has a tileId),
    // regardless of linking status (CREATED, WARNING_PARTIAL, or ERROR_LINKING)
    if (item.tileId) {
      buttons.push({
        id: 'configure',
        text: 'Configure',
        colors: ['white', 'indigo'],
        size: 'small',
        callback: () => this.configureTile(item)
      });

      buttons.push({
        id: 'apps',
        text: 'Link/Unlink Apps',
        colors: ['indigo'],
        size: 'small',
        multiselect: {
          onReady: (og: OptionGroupMultiSelect) => {
            this.currentOptionGroup = og;
            og.saveInitialSelection();
            // Check permission once and cache it
            this.permissionsService.queryPermissions({
              UserID: this.session.userId,
              ObjectID: item.tileId!,
              ObjectType: ObjectType.TILE,
              Permissions: [Permission.CAN_CONFIGURE],
            }).subscribe({
              next: (response) => {
                og.canConfigure = !!response.Results[0]?.Allowed;
              },
              error: () => {
                og.canConfigure = false;
              },
            });
          },
          onClickedOption: () => {
            if (this.currentOptionGroup && this.currentOptionGroup.canConfigure === false) {
              this.shadeToolsService.errorDialog('You do not have permission to link/unlink apps for this tile.');
              return;
            }
            // If allowed or not yet loaded, let the UI proceed
          },
          onClickedOutside: () => {
            // Store a reference to the current option group before any async operations
            const optionGroup = this.currentOptionGroup;
            // Set to null after capturing the reference to prevent concurrent modifications
            this.currentOptionGroup = null;

            if (optionGroup?.hasChanges()) {
              // Store selected and unselected options immediately to avoid them becoming null later
              const selected = optionGroup.getSelected() || [];
              const unselected = optionGroup.getUnselected() || [];

              this.shadeToolsService.startDialog(
                'Links to Apps Changed',
                'Do you want to save the changes you made to this tile\'s linked apps?',
                [
                  {
                    text: 'Save Changes',
                    color: DialogBoxButtonColor.Green,
                    callback: () => {
                      // Update app links using the tiles service
                      if (selected?.length || unselected?.length) {
                        this.tilesService.updateAppLinks$(
                          item.tileId!,
                          selected.map(o => o.id),
                          unselected.map(o => o.id),
                        ).subscribe({
                          next: () => this.shadeToolsService.closeAll(),
                          error: () => this.shadeToolsService.errorDialog(),
                        });
                      }
                    },
                  },
                  {
                    text: 'Ignore Changes',
                    color: DialogBoxButtonColor.Alt,
                    callback: () => this.shadeToolsService.closeAll(),
                  },
                ],
                false, false, false, false,
              );
            }
          },
          objectType: {
            apps: {
              enabled: true,
              preselectAppsWithTileId: item.tileId!,
            },
          },
        },
      });
    }

    return buttons;
  }

  private configureTile(item: TileProcessingItem): void {
    if (item.tileId) {
      this.editingTile = item.tileId;
    }
  }

  onTileEditorClosed(): void {
    // Refresh tile data when editor is closed
    if (this.editingTile) {
      const tileId = this.editingTile;
      this.tilesService.fetchTile$(tileId).subscribe({
        next: (updatedTile) => {
          // Update tile object in processing items - use currentService based on mode
          this.currentService.processingItems.update(items => {
            return items.map(item => {
              if (item.tileId === tileId) {
                return {
                  ...item,
                  tile: updatedTile
                };
              }
              return item;
            });
          });
        },
        error: (err) => {
          console.error('Failed to fetch updated tile:', err);
        }
      });
    }
    this.editingTile = EMPTY_ID;
  }

  deleteAllCreatedTiles(): void {
    // Only available in creation mode (template already guards this)
    const createdTiles = this.currentService.processingItems().filter(item => item.tileId);
    const tileCount = createdTiles.length;

    if (tileCount === 0) {
      return;
    }

    this.shadeToolsService.startDialog(
      '<i class="fa-regular fa-trash"></i> Delete All Created Tiles?',
      `This will permanently delete all ${tileCount} tile${tileCount > 1 ? 's' : ''} that were created during this CSV processing. ` +
      'This action cannot be undone.',
      [
        {
          text: 'Delete All',
          color: DialogBoxButtonColor.Red,
          callback: () => {
            this.performTileDeletion(createdTiles);
          }
        },
        {
          text: 'Cancel',
          color: DialogBoxButtonColor.Alt,
          callback: () => {
            // Just close the dialog
          }
        }
      ],
      false, false, true, true
    );
  }

  private performTileDeletion(tilesToDelete: TileProcessingItem[]): void {
    let deletedCount = 0;
    let errorCount = 0;
    const totalCount = tilesToDelete.length;
    let isDeleting = true;
    const deletedItemIds: string[] = []; // Track deleted item IDs for batched update

    // Show initial processing dialog
    this.shadeToolsService.startDialog(
      '<i class="fa-regular fa-loader fa-spin"></i> Deleting Tiles',
      `Deleting ${totalCount} tile${totalCount > 1 ? 's' : ''}...`,
      [
        {
          text: 'Cancel',
          color: DialogBoxButtonColor.Alt,
          callback: () => {
            isDeleting = false;
          }
        }
      ],
      false, false, true, true
    );

    // Delete tiles in batches for better performance
    const BATCH_SIZE = 5;

    from(tilesToDelete).pipe(
      takeWhile(() => isDeleting),
      mergeMap(item => {
        if (!item.tileId) {
          return of({ success: false, item });
        }

        return this.tilesService.deleteTile$(item.tileId).pipe(
          map(() => ({ success: true, item })),
          catchError(error => {
            console.error(`Failed to delete tile ${item.tileId}:`, error);
            return of({ success: false, item });
          })
        );
      }, BATCH_SIZE),
      tap(result => {
        if (result.success) {
          deletedCount++;
          // Track deleted item ID for batched update
          deletedItemIds.push(result.item.id);
        } else if (result.item.tileId) {
          errorCount++;
        }
      }),
      finalize(() => {
        // Batch update: Remove all deleted items in a single signal update
        if (deletedItemIds.length > 0) {
          const items = this.currentService.processingItems();
          const remainingItems = items.filter(item => !deletedItemIds.includes(item.id));
          this.currentService.processingItems.set(remainingItems);
        }

        if (isDeleting) {
          this.onDeletionComplete(deletedCount, errorCount, totalCount);
        }
      })
    ).subscribe();
  }

  private onDeletionComplete(deletedCount: number, errorCount: number, totalCount: number): void {
    this.shadeToolsService.closeAll();

    if (errorCount === 0) {
      this.shadeToolsService.startDialog(
        '<i class="fa-regular fa-circle-check"></i> Tiles Deleted',
        `Successfully deleted all ${deletedCount} tile${deletedCount > 1 ? 's' : ''}.`,
        [
          {
            text: 'OK',
            color: DialogBoxButtonColor.Green,
            callback: () => {
              // If all tiles were deleted, close the processing dialog
              const items = this.currentService.processingItems();
              if (items.length === 0 || items.every(item => !item.tileId)) {
                this.close.emit();
              }
            }
          }
        ],
        false, false, true, true
      );
    } else if (deletedCount > 0) {
      this.shadeToolsService.startDialog(
        '<i class="fa-regular fa-triangle-exclamation"></i> Partial Success',
        `Deleted ${deletedCount} of ${totalCount} tiles. ${errorCount} deletion${errorCount > 1 ? 's' : ''} failed.`,
        [
          {
            text: 'OK',
            color: DialogBoxButtonColor.Default,
            callback: () => {}
          }
        ],
        false, false, true, true
      );
    } else {
      this.shadeToolsService.errorDialog(
        `Failed to delete tiles. Try again from the Controls view later.`
      );
    }
  }

  revertUpdates(): void {
    const items = this.bulkTileConfigService.processingItems();
    const revertableCount = items.filter(item =>
      item.originalTile &&
      item.status === TileProcessingStatus.UPDATED
    ).length;

    if (revertableCount === 0) {
      return;
    }

    this.shadeToolsService.startDialog(
      '<i class="fa-regular fa-rotate-left"></i> Revert All Updates?',
      `This will revert all ${revertableCount} tile${revertableCount > 1 ? 's' : ''} back to their original configuration before the CSV updates. ` +
      'This action cannot be undone.',
      [
        {
          text: 'Revert All',
          color: DialogBoxButtonColor.Yellow,
          callback: () => {
            this.performRevert(revertableCount);
          }
        },
        {
          text: 'Cancel',
          color: DialogBoxButtonColor.Alt,
          callback: () => {
            // Just close the dialog
          }
        }
      ],
      false, false, true, true
    );
  }

  private performRevert(totalCount: number): void {
    // Show processing dialog
    this.shadeToolsService.startDialog(
      '<i class="fa-regular fa-loader fa-spin"></i> Reverting Updates',
      `Reverting ${totalCount} tile${totalCount > 1 ? 's' : ''}...`,
      [],
      false, false, true, true
    );

    // Subscribe to the revert operation
    this.bulkTileConfigService.revertUpdates().subscribe({
      next: (result) => {
        this.onRevertComplete(result.success, result.failed, totalCount);
      },
      error: (error) => {
        console.error('Revert operation failed:', error);
        this.shadeToolsService.closeAll();
        this.shadeToolsService.errorDialog('Failed to revert updates. Please try again.');
      }
    });
  }

  private onRevertComplete(successCount: number, failedCount: number, totalCount: number): void {
    this.shadeToolsService.closeAll();

    // Get tile IDs for navigating back to configuration
    const tileIds = this.bulkTileConfigService.processingItems()
      .filter(item => item.tileId)
      .map(item => item.tileId!);

    if (failedCount === 0) {
      // All reverts successful
      this.shadeToolsService.startDialog(
        '<i class="fa-regular fa-circle-check"></i> Updates Reverted',
        `Successfully reverted all ${successCount} tile${successCount > 1 ? 's' : ''} to their original configuration.`,
        [
          {
            text: 'Upload New CSV',
            color: DialogBoxButtonColor.Green,
            callback: () => {
              this.shadeToolsService.closeAll();
              this.bulkModalService.backToConfiguration();
            }
          },
          {
            text: 'Close',
            color: DialogBoxButtonColor.Alt,
            callback: () => {
              this.shadeToolsService.closeAll();
              this.bulkModalService.close();
            }
          }
        ],
        false, false, true, true
      );
    } else if (successCount > 0) {
      // Partial success
      this.shadeToolsService.startDialog(
        '<i class="fa-regular fa-triangle-exclamation"></i> Partial Success',
        `Reverted ${successCount} of ${totalCount} tiles. ${failedCount} revert${failedCount > 1 ? 's' : ''} failed.`,
        [
          {
            text: 'Upload New CSV',
            color: DialogBoxButtonColor.Default,
            callback: () => {
              this.shadeToolsService.closeAll();
              this.bulkModalService.backToConfiguration();
            }
          },
          {
            text: 'Close',
            color: DialogBoxButtonColor.Alt,
            callback: () => {
              this.shadeToolsService.closeAll();
              this.bulkModalService.close();
            }
          }
        ],
        false, false, true, true
      );
    } else {
      // All reverts failed
      this.shadeToolsService.errorDialog(
        'Failed to revert updates. Please try again or configure tiles individually.'
      );
    }
  }

}
